import React, { useState } from "react";
import { C } from "../constants";
import { FIELD_TONES, FancyTextarea, FieldGroup } from "./FormControls";
import ProfileSuggestionChips from "./ProfileSuggestionChips";
import SkillRatingsEditor, { normalizeItemRatings } from "./SkillRatingsEditor";
import { UploadZone } from "./UploadZone";

const TABS = [
  { id: "goals", label: "Goals", icon: "ti-flag", tone: "ink" },
  { id: "capability", label: "Strengths", icon: "ti-user-check", tone: "navy" },
  { id: "difficulties", label: "Difficulties", icon: "ti-alert-triangle", tone: "slate" },
  { id: "interest", label: "Likes", icon: "ti-heart", tone: "coral" },
  { id: "dislikes", label: "Dislikes", icon: "ti-thumb-down", tone: "amber" },
  { id: "domain", label: "Domain", icon: "ti-world", tone: "teal" },
];

export function assessFilledCount(m) {
  return [
    m?.assessment,
    m?.capability,
    m?.difficulties,
    m?.interest,
    m?.dislikes,
    m?.domain,
  ].filter((v) => String(v || "").trim()).length;
}

/**
 * Collapsible + tabbed assessment editor for one team member.
 * Keeps Basics readable: only one persona section open at a time.
 */
export default function MemberAssessPanel({
  memberIndex = 0,
  profile = {},
  updateMember,
  setMemberField,
  setMemberRatings,
  files = {},
  setFiles,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState("goals");
  const filledN = assessFilledCount(profile);
  const i = memberIndex;
  const ratings = normalizeItemRatings(profile);

  return (
    <div
      style={{
        marginTop: 14,
        borderRadius: 14,
        border: `1.5px solid ${open ? "#1B4F7266" : C.border}`,
        overflow: "hidden",
        background: open ? "#fff" : C.surfaceAlt,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "12px 14px",
          background: open
            ? "linear-gradient(90deg, #E7F0F6 0%, #FFFFFF 100%)"
            : "transparent",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              flexShrink: 0,
              background: "#E7F0F6",
              color: "#1B4F72",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i className="ti ti-chart-bar" style={{ fontSize: 15 }} />
          </span>
          <span>
            <span style={{ display: "block", fontSize: 13.5, fontWeight: 800, color: C.text }}>
              Assessment & persona
            </span>
            <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: C.muted, marginTop: 2 }}>
              {filledN
                ? `${filledN} of 6 filled — tap to ${open ? "close" : "edit"}`
                : "Goals, strengths, likes — open one section at a time"}
            </span>
          </span>
        </span>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 800,
            color: filledN === 6 ? "#0B5C2E" : "#143B56",
            background: filledN === 6 ? C.successBg : "#E7F0F6",
            borderRadius: 999,
            padding: "4px 10px",
            flexShrink: 0,
          }}
        >
          {filledN}/6
        </span>
        <span style={{ fontSize: 16, fontWeight: 700, color: C.muted, flexShrink: 0 }}>
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open ? (
        <div style={{ padding: "4px 16px 18px" }}>
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 12,
              paddingTop: 2,
            }}
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              const tone = FIELD_TONES[t.tone] || FIELD_TONES.blue;
              const hasVal =
                t.id === "goals"
                  ? String(profile.assessment || "").trim()
                  : String(profile[t.id] || "").trim();
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  style={{
                    border: `1.5px solid ${active ? tone.accent : C.border}`,
                    background: active ? tone.soft : "#fff",
                    color: active ? tone.chip : C.muted,
                    borderRadius: 999,
                    padding: "7px 12px",
                    fontSize: 12,
                    fontWeight: 750,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <i className={`ti ${t.icon}`} style={{ fontSize: 13 }} />
                  {t.label}
                  {hasVal ? (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: C.success,
                        display: "inline-block",
                      }}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          {tab === "goals" && (
            <FieldGroup title="Individual assessment & goals" icon="ti-flag" tone="ink" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 8px", lineHeight: 1.4 }}>
                Personal targets — in addition to shared team goals above.
              </p>
              <FancyTextarea
                tone="ink"
                value={profile.assessment || ""}
                onChange={(e) => updateMember(i, "assessment", e.target.value)}
                placeholder="e.g. Aim SDE-1 · focus Graphs + System Design · AWS CCP by Oct"
                style={{ minHeight: 72 }}
              />
              {setFiles && (
                <UploadZone
                  paramKey={`m${i}_assessment`}
                  files={files}
                  setFiles={setFiles}
                  compact
                  hint="Personal JD / goals PDF or Excel"
                />
              )}
            </FieldGroup>
          )}

          {tab === "capability" && (
            <FieldGroup title="Capability — strengths" icon="ti-user-check" tone="teal" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 6px" }}>
                Soft + tech + work style strengths
              </p>
              <FancyTextarea
                tone="teal"
                value={profile.capability || ""}
                onChange={(e) => setMemberField(i, "capability", e.target.value)}
                placeholder="e.g. Strong communicator; solid Python basics…"
                rows={2}
                style={{ minHeight: 56, marginBottom: 4 }}
              />
              <ProfileSuggestionChips
                kind="capability"
                value={profile.capability || ""}
                onChange={(v) => setMemberField(i, "capability", v)}
              />
              <SkillRatingsEditor
                kind="capability"
                skills={profile.capability || ""}
                ratings={ratings.capability}
                onChange={(r) => setMemberRatings(i, "capability", r)}
              />
              {setFiles && (
                <UploadZone
                  paramKey={`m${i}_capability`}
                  files={files}
                  setFiles={setFiles}
                  compact
                  hint="Resume / strengths PDF optional"
                />
              )}
            </FieldGroup>
          )}

          {tab === "difficulties" && (
            <FieldGroup title="Difficulties" icon="ti-alert-triangle" tone="slate" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 6px" }}>
                Weak spots + medium (e.g. Tamil medium)
              </p>
              <FancyTextarea
                tone="slate"
                value={profile.difficulties || ""}
                onChange={(e) => setMemberField(i, "difficulties", e.target.value)}
                placeholder="e.g. Tamil medium; weak in DSA; exam anxiety…"
                rows={2}
                style={{ minHeight: 56, marginBottom: 4 }}
              />
              <ProfileSuggestionChips
                kind="difficulties"
                value={profile.difficulties || ""}
                onChange={(v) => setMemberField(i, "difficulties", v)}
              />
              <SkillRatingsEditor
                kind="difficulties"
                skills={profile.difficulties || ""}
                ratings={ratings.difficulties}
                onChange={(r) => setMemberRatings(i, "difficulties", r)}
              />
            </FieldGroup>
          )}

          {tab === "interest" && (
            <FieldGroup title="Interests — likes & activities" icon="ti-heart" tone="coral" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 6px" }}>
                Sports, dance, music, hobbies
              </p>
              <FancyTextarea
                tone="coral"
                value={profile.interest || ""}
                onChange={(e) => setMemberField(i, "interest", e.target.value)}
                placeholder="e.g. Cricket, dance, guitar…"
                rows={2}
                style={{ minHeight: 56, marginBottom: 4 }}
              />
              <ProfileSuggestionChips
                kind="interest"
                value={profile.interest || ""}
                onChange={(v) => setMemberField(i, "interest", v)}
              />
              <SkillRatingsEditor
                kind="interest"
                skills={profile.interest || ""}
                ratings={ratings.interest}
                onChange={(r) => setMemberRatings(i, "interest", r)}
              />
              {setFiles && (
                <UploadZone
                  paramKey={`m${i}_interest`}
                  files={files}
                  setFiles={setFiles}
                  compact
                  hint="Optional notes sheet"
                />
              )}
            </FieldGroup>
          )}

          {tab === "dislikes" && (
            <FieldGroup title="Dislikes / avoid" icon="ti-thumb-down" tone="coral" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 6px" }}>
                What drains them — respected in Mini Build
              </p>
              <FancyTextarea
                tone="coral"
                value={profile.dislikes || ""}
                onChange={(e) => setMemberField(i, "dislikes", e.target.value)}
                placeholder="e.g. Long lectures without practice…"
                rows={2}
                style={{ minHeight: 56, marginBottom: 4 }}
              />
              <ProfileSuggestionChips
                kind="dislikes"
                value={profile.dislikes || ""}
                onChange={(v) => setMemberField(i, "dislikes", v)}
              />
              <SkillRatingsEditor
                kind="dislikes"
                skills={profile.dislikes || ""}
                ratings={ratings.dislikes}
                onChange={(r) => setMemberRatings(i, "dislikes", r)}
              />
            </FieldGroup>
          )}

          {tab === "domain" && (
            <FieldGroup title="Domain / direction" icon="ti-world" tone="amber" style={{ marginTop: 0 }}>
              <p style={{ fontSize: 11, color: C.muted, margin: "0 0 6px" }}>
                Industry, tech focus, or role path
              </p>
              <FancyTextarea
                tone="amber"
                value={profile.domain || ""}
                onChange={(e) => setMemberField(i, "domain", e.target.value)}
                placeholder="e.g. FinTech + full-stack…"
                rows={2}
                style={{ minHeight: 56, marginBottom: 4 }}
              />
              <ProfileSuggestionChips
                kind="domain"
                value={profile.domain || ""}
                onChange={(v) => setMemberField(i, "domain", v)}
              />
              <SkillRatingsEditor
                kind="domain"
                skills={profile.domain || ""}
                ratings={ratings.domain}
                onChange={(r) => setMemberRatings(i, "domain", r)}
              />
              {setFiles && (
                <UploadZone
                  paramKey={`m${i}_domain`}
                  files={files}
                  setFiles={setFiles}
                  compact
                  hint="Optional domain sheet"
                />
              )}
            </FieldGroup>
          )}
        </div>
      ) : null}
    </div>
  );
}
